import * as React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "lib/utils";
import { EmptyState, type EmptyStateProps } from "components/ui/empty-state";
import { SkeletonTable } from "components/ui/loading-state";

/* ══════════════════════════════════════════════════════════════════════════
   DataTable

   List pages each rebuilt: the <table> chrome, a sticky header, a checkbox
   selection column, sort affordances, a "no rows" block and — separately — a
   card layout for phones. That is five things to get wrong per page.

   This declares columns once and renders both presentations from them:
   a real table on desktop, stacked cards on phones (label/value pairs from
   the same column definitions), with selection, sorting, loading and empty
   states handled centrally.
   ══════════════════════════════════════════════════════════════════════════ */

export interface Column<T> {
    id: string;
    header: React.ReactNode;
    /** Cell renderer. Receives the row and its index. */
    cell: (row: T, index: number) => React.ReactNode;
    /** Enables the sort control on this column's header */
    sortable?: boolean;
    /** Tailwind width class, e.g. "w-48" */
    width?: string;
    align?: "left" | "right" | "center";
    /** Hide below this breakpoint on the desktop table */
    hideBelow?: "sm" | "md" | "lg" | "xl";
    /**
     * Role in the phone card layout:
     *  `title`  — the card's headline
     *  `meta`   — subtitle under the headline
     *  `field`  — a label/value row (default)
     *  `action` — pinned to the card's top-right
     *  `hidden` — omitted on phones
     */
    card?: "title" | "meta" | "field" | "action" | "hidden";
}

export interface DataTableProps<T> {
    columns: Column<T>[];
    rows: T[];
    /** Stable key per row */
    getRowId: (row: T, index: number) => string;

    isLoading?: boolean;
    /** Row count for the loading skeleton */
    skeletonRows?: number;

    /** Shown when `rows` is empty and not loading */
    empty?: React.ReactNode | EmptyStateProps;

    /** Controlled selection. Omit to disable the checkbox column. */
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
    /** Rows that cannot be selected */
    isRowSelectable?: (row: T) => boolean;

    sortBy?: string;
    sortDirection?: "asc" | "desc";
    onSortChange?: (columnId: string, direction: "asc" | "desc") => void;

    onRowClick?: (row: T) => void;
    /** Marks a row visually (e.g. the one open in a drawer) */
    isRowActive?: (row: T) => boolean;

    /** Row height. `compact` fits ~40% more rows on screen. */
    density?: "comfortable" | "compact";
    /** Keeps the header visible while the body scrolls */
    stickyHeader?: boolean;
    /** Rendered above the table when at least one row is selected */
    bulkActions?: (selectedIds: string[]) => React.ReactNode;

    caption?: string;
    className?: string;
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" } as const;
const HIDE = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
    xl: "hidden xl:table-cell",
} as const;

function Checkbox({
    checked,
    indeterminate,
    onChange,
    label,
    disabled,
}: {
    checked: boolean;
    indeterminate?: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    disabled?: boolean;
}) {
    const ref = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
    }, [indeterminate, checked]);

    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            aria-label={label}
            onChange={(e) => onChange(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className={cn(
                "w-4 h-4 rounded border-line-strong text-brand cursor-pointer",
                "focus:ring-2 focus:ring-offset-0 focus:ring-[color:var(--ring)]",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "accent-[color:var(--brand)]"
            )}
        />
    );
}

export function DataTable<T>({
    columns,
    rows,
    getRowId,
    isLoading = false,
    skeletonRows = 8,
    empty,
    selectedIds,
    onSelectionChange,
    isRowSelectable,
    sortBy,
    sortDirection = "asc",
    onSortChange,
    onRowClick,
    isRowActive,
    density = "comfortable",
    stickyHeader = true,
    bulkActions,
    caption,
    className,
}: DataTableProps<T>) {
    const selectable = Array.isArray(selectedIds) && typeof onSelectionChange === "function";
    const selected = React.useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

    const selectableRows = React.useMemo(
        () => rows.filter((row) => (isRowSelectable ? isRowSelectable(row) : true)),
        [rows, isRowSelectable]
    );
    const selectableIds = React.useMemo(
        () => selectableRows.map((row, i) => getRowId(row, i)),
        [selectableRows, getRowId]
    );
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
    const someSelected = selectableIds.some((id) => selected.has(id));

    const toggleAll = (checked: boolean) => {
        if (!onSelectionChange) return;
        if (checked) {
            onSelectionChange(Array.from(new Set([...(selectedIds ?? []), ...selectableIds])));
        } else {
            const remove = new Set(selectableIds);
            onSelectionChange((selectedIds ?? []).filter((id) => !remove.has(id)));
        }
    };

    const toggleRow = (id: string, checked: boolean) => {
        if (!onSelectionChange) return;
        onSelectionChange(
            checked ? [...(selectedIds ?? []), id] : (selectedIds ?? []).filter((x) => x !== id)
        );
    };

    const handleSort = (col: Column<T>) => {
        if (!col.sortable || !onSortChange) return;
        const nextDir = sortBy === col.id && sortDirection === "asc" ? "desc" : "asc";
        onSortChange(col.id, nextDir);
    };

    const rowPad = density === "compact" ? "py-2" : "py-3.5";

    if (isLoading) {
        return <SkeletonTable rows={skeletonRows} columns={Math.min(columns.length, 6)} className={className} />;
    }

    if (!rows.length) {
        if (React.isValidElement(empty)) return <>{empty}</>;
        const props = (empty ?? { title: "Nothing here yet" }) as EmptyStateProps;
        return <EmptyState {...props} className={cn(className, props.className)} />;
    }

    const cardColumns = columns.filter((c) => c.card !== "hidden");
    const titleCol = cardColumns.find((c) => c.card === "title") ?? cardColumns[0];
    const metaCols = cardColumns.filter((c) => c.card === "meta");
    const actionCols = cardColumns.filter((c) => c.card === "action");
    const fieldCols = cardColumns.filter(
        (c) => c !== titleCol && c.card !== "meta" && c.card !== "action"
    );

    return (
        <div className={cn("w-full min-w-0", className)}>
            {selectable && bulkActions && selected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 mb-2 rounded-xl bg-brand-subtle border border-brand-border">
                    <span className="text-ui font-semibold text-brand-on-subtle tabular-nums">
                        {selected.size} selected
                    </span>
                    <div className="flex items-center gap-2 ml-auto flex-wrap">
                        {bulkActions(selectedIds ?? [])}
                        <button
                            type="button"
                            onClick={() => onSelectionChange?.([])}
                            className="text-meta font-semibold text-content-muted hover:text-content px-2 py-1 rounded-lg hover:bg-surface-hover transition-colors focus-ring"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {/* ── Desktop: real table ── */}
            <div className="hidden md:block rounded-xl bg-surface border border-line shadow-card overflow-hidden">
                <div className="overflow-x-auto overflow-y-visible">
                    <table className="w-full border-collapse">
                        {caption && <caption className="sr-only">{caption}</caption>}
                        <thead
                            className={cn(
                                "bg-surface-muted border-b border-line",
                                stickyHeader && "sticky top-0 z-10"
                            )}
                        >
                            <tr>
                                {selectable && (
                                    <th scope="col" className="w-10 pl-4 pr-2">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            onChange={toggleAll}
                                            label="Select all rows"
                                        />
                                    </th>
                                )}
                                {columns.map((col) => {
                                    const isSorted = sortBy === col.id;
                                    const SortIcon = !col.sortable
                                        ? null
                                        : !isSorted
                                            ? ChevronsUpDown
                                            : sortDirection === "asc"
                                                ? ChevronUp
                                                : ChevronDown;
                                    return (
                                        <th
                                            key={col.id}
                                            scope="col"
                                            style={undefined}
                                            aria-sort={
                                                isSorted
                                                    ? sortDirection === "asc"
                                                        ? "ascending"
                                                        : "descending"
                                                    : col.sortable
                                                        ? "none"
                                                        : undefined
                                            }
                                            className={cn(
                                                "h-11 px-4 align-middle text-2xs font-bold uppercase tracking-wider text-content-muted whitespace-nowrap",
                                                ALIGN[col.align ?? "left"],
                                                col.width,
                                                col.hideBelow && HIDE[col.hideBelow]
                                            )}
                                        >
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(col)}
                                                    className={cn(
                                                        "inline-flex items-center gap-1 rounded hover:text-content transition-colors focus-ring",
                                                        isSorted && "text-brand"
                                                    )}
                                                >
                                                    {col.header}
                                                    {SortIcon && <SortIcon className="w-3 h-3" aria-hidden="true" />}
                                                </button>
                                            ) : (
                                                col.header
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const id = getRowId(row, index);
                                const isSelected = selected.has(id);
                                const active = isRowActive?.(row);
                                const clickable = typeof onRowClick === "function";
                                return (
                                    <tr
                                        key={id}
                                        data-state={isSelected ? "selected" : undefined}
                                        onClick={clickable ? () => onRowClick?.(row) : undefined}
                                        onKeyDown={
                                            clickable
                                                ? (e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        onRowClick?.(row);
                                                    }
                                                }
                                                : undefined
                                        }
                                        tabIndex={clickable ? 0 : undefined}
                                        className={cn(
                                            "border-b border-line-subtle last:border-0 transition-colors",
                                            clickable && "cursor-pointer focus:outline-none focus-visible:bg-surface-hover",
                                            isSelected ? "bg-brand-subtle" : active ? "bg-surface-hover" : "hover:bg-surface-hover"
                                        )}
                                    >
                                        {selectable && (
                                            <td className="w-10 pl-4 pr-2">
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={isRowSelectable ? !isRowSelectable(row) : false}
                                                    onChange={(checked) => toggleRow(id, checked)}
                                                    label={`Select row ${index + 1}`}
                                                />
                                            </td>
                                        )}
                                        {columns.map((col) => (
                                            <td
                                                key={col.id}
                                                className={cn(
                                                    "px-4 align-middle text-ui text-content-secondary",
                                                    rowPad,
                                                    ALIGN[col.align ?? "left"],
                                                    col.hideBelow && HIDE[col.hideBelow]
                                                )}
                                            >
                                                {col.cell(row, index)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Phone: the same columns as stacked cards ── */}
            <ul className="md:hidden space-y-2.5">
                {rows.map((row, index) => {
                    const id = getRowId(row, index);
                    const isSelected = selected.has(id);
                    return (
                        <li
                            key={id}
                            className={cn(
                                "rounded-xl bg-surface border shadow-card p-3.5 transition-colors",
                                isSelected ? "border-brand-border bg-brand-subtle" : "border-line"
                            )}
                        >
                            <div className="flex items-start gap-2.5 min-w-0">
                                {selectable && (
                                    <span className="pt-0.5 flex-shrink-0">
                                        <Checkbox
                                            checked={isSelected}
                                            disabled={isRowSelectable ? !isRowSelectable(row) : false}
                                            onChange={(checked) => toggleRow(id, checked)}
                                            label={`Select item ${index + 1}`}
                                        />
                                    </span>
                                )}

                                <button
                                    type="button"
                                    disabled={!onRowClick}
                                    onClick={() => onRowClick?.(row)}
                                    className={cn(
                                        "min-w-0 flex-1 text-left rounded-lg focus-ring",
                                        !onRowClick && "cursor-default"
                                    )}
                                >
                                    <div className="text-ui-lg font-semibold text-content truncate">
                                        {titleCol?.cell(row, index)}
                                    </div>
                                    {metaCols.length > 0 && (
                                        <div className="mt-0.5 flex items-center gap-2 flex-wrap text-meta text-content-muted min-w-0">
                                            {metaCols.map((col) => (
                                                <span key={col.id} className="truncate">
                                                    {col.cell(row, index)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </button>

                                {actionCols.length > 0 && (
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {actionCols.map((col) => (
                                            <React.Fragment key={col.id}>{col.cell(row, index)}</React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {fieldCols.length > 0 && (
                                <dl className="mt-3 pt-3 border-t border-line-subtle grid grid-cols-2 gap-x-3 gap-y-2">
                                    {fieldCols.map((col) => (
                                        <div key={col.id} className="min-w-0">
                                            <dt className="text-2xs font-bold uppercase tracking-wider text-content-subtle truncate">
                                                {col.header}
                                            </dt>
                                            <dd className="text-meta text-content-secondary mt-0.5 truncate">
                                                {col.cell(row, index)}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default DataTable;
